import { getToken, getRefreshToken, refreshAccessToken, API_BASE_URL, hostOf } from './client.ts';
import { getRoomSlug } from '../lib/roomSelection.ts';
import type { StrapiErrorBody } from './types.ts';

/**
 * The one SSE client (SPEC §4.4, ARCHITECTURE.md §6). Every subsystem that needs live updates
 * consumes THIS client rather than opening a second stream or polling.
 *
 * Deliberately not `EventSource`: it cannot send an `Authorization` header, and a JWT must never
 * appear in a query string. This opens the stream with `fetch()` and reads the body as a
 * `ReadableStream`, exactly as SPEC §4.4 specifies.
 */

export type ConnectionState = 'connecting' | 'online' | 'degraded' | 'offline' | 'reauth';

export interface RealtimeFrame {
  seq: number;
  event: string;
  channel: string;
  ts: number;
  payload: unknown;
}

const HEARTBEAT_TIMEOUT_MS = 30_000; // SPEC §4.4: "30s with no frame -> degraded"
const BACKOFF_STEPS_MS = [1000, 2000, 4000, 8000, 16000, 30000]; // SPEC §4.4 reconnect backoff

/** attempt 0 -> 1s, attempt 5+ -> capped at 30s, each ±20% jitter. Pure and testable. */
export function backoffDelayMs(attempt: number, rand: () => number = Math.random): number {
  const base = BACKOFF_STEPS_MS[Math.min(Math.max(attempt, 0), BACKOFF_STEPS_MS.length - 1)];
  const jitter = base * 0.2 * (rand() * 2 - 1);
  return Math.max(0, Math.round(base + jitter));
}

/**
 * Parses one `\n\n`-delimited SSE block (SPEC §4.4 frame format: `id:`/`event:`/`data:` lines).
 * Returns null for a bare heartbeat comment (`:hb`) or any block missing a `data:` line — pure
 * and testable without a network.
 */
export function parseSSEBlock(block: string): RealtimeFrame | null {
  let id: string | null = null;
  let event = 'message';
  let data: string | null = null;

  for (const rawLine of block.split('\n')) {
    const line = rawLine.trimEnd();
    if (!line || line.startsWith(':')) continue; // comment / heartbeat
    const colon = line.indexOf(':');
    if (colon === -1) continue;
    const field = line.slice(0, colon);
    const value = line.slice(colon + 1).replace(/^ /, '');
    if (field === 'id') id = value;
    else if (field === 'event') event = value;
    else if (field === 'data') data = value;
  }

  if (data === null) return null;

  let parsed: { seq?: number; ts?: number; channel?: string; payload?: unknown };
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }

  return {
    seq: parsed.seq ?? (id !== null ? Number(id) : 0),
    event,
    channel: parsed.channel ?? 'settings',
    ts: parsed.ts ?? Date.now(),
    payload: parsed.payload,
  };
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      resolve();
    });
  });
}

// `hostOf` now lives in client.ts (this used to be an identical, independently-defined copy) —
// re-exported here so `realtime.test.ts` and `api/guest.ts` keep importing it from this module,
// unchanged.
export { hostOf };

/**
 * remote-request additions (additive-only — nothing above this line is touched). `channels`/
 * `deviceId`/`useAuth` let a second, unauthenticated instance scope itself to the guest's own
 * request page (api/guest.ts) instead of the one shared `realtimeClient` singleton below, which
 * stays exactly as it was: always authenticated, always every channel the operator role allows.
 */
export interface RealtimeOptions {
  baseUrl?: string;
  /** Explicit channel list sent as `?channels=a,b` (realtime controller's `ctx.query.channels`).
   * Omitted entirely for the default operator client, which gets every channel its role allows. */
  channels?: string[];
  /** A guest's `localStorage` device id (lib/deviceId.ts), sent as `?deviceId=` so `request.mine`
   * frames can find this one phone (server hashes it — src/utils/device-id.js). */
  deviceId?: string;
  /** MVP-472 follow-up 4. The guest's join code, sent as `?joinCode=` so the server can resolve
   * which ROOM this phone is sitting in (its session's room) and stop sending it another room's
   * `queue.changed` / `session.updated`. The same code that is already in this page's URL — not a
   * secret, and the server never trusts it for anything but scope.
   *
   * Omitted by the operator client, whose room comes from its own account instead. */
  joinCode?: string;
  /** `false` for the guest connection: never attach `Authorization`, even if an operator happens
   * to be signed in on the SAME browser (api/guest.ts's own doc comment explains the leak this
   * avoids). Defaults to `true`, preserving every existing (operator) call site unchanged. */
  useAuth?: boolean;
}

export class RealtimeClient {
  private baseUrl: string;
  private channels?: string[];
  private deviceId?: string;
  private joinCode?: string;
  private useAuth: boolean;
  private controller: AbortController | null = null;
  private sleepController: AbortController | null = null;
  private lastEventId: string | null = null;
  private attempt = 0;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private state: ConnectionState = 'connecting';
  private stopped = true;
  // Bumped by every start()/stop() transition (round-2 fix, Notion task 48). React StrictMode's
  // dev-only mount -> cleanup -> mount runs start(); stop(); start() synchronously, so a lone
  // `this.stopped` flag is not enough to tell a genuinely-stopped loop apart from a loop whose
  // instance was restarted out from under it: the second start() clears `this.stopped` back to
  // `false` before the first loop's in-flight `connectOnce()` ever gets a chance to observe it,
  // so the old ("zombie") loop sees `!this.stopped` and keeps running forever, sharing (and
  // stomping on) `this.controller`/`this.attempt`/`this.state` with the new loop — proven live in
  // Chrome as a permanently-stuck "Offline" indicator and `health.sse.clients` sitting at 2 for a
  // single tab. Each `connectLoop()` call captures its own generation number once and re-checks it
  // at every await boundary; the instant it no longer matches `this.generation`, that loop is dead
  // and returns without touching any shared field again.
  private generation = 0;
  // R10-P2-1 fix: without this, EVERY reconnect attempt during a sustained 401/403 outage called
  // `refreshAccessToken()` again — each attempt captures a freshly-rotated token via `getToken()`
  // at the top of `connectOnce()`, so the old `getToken() !== token` dedupe (which only catches a
  // DIFFERENT connection racing us) can never fire here: it's always OUR OWN prior refresh that
  // rotated the token. Confirmed live: 4 refreshes in 16s against a simulated FORBIDDEN_CHANNEL
  // outage, rotating the one-time-use refresh token every backoff step. This flag caps it at one
  // refresh per outage; it is cleared only by an actual `hello` frame (a genuinely restored
  // stream), never merely by reaching the `online` state, so a single successful refresh gets
  // exactly one retry to prove itself before the flag can be earned back.
  private refreshedThisOutage = false;
  private stateListeners = new Set<(state: ConnectionState) => void>();
  private frameListeners = new Set<(frame: RealtimeFrame) => void>();

  constructor(options: string | RealtimeOptions = {}) {
    // Back-compat: `realtime.test.ts` (not owned by this task, not to be edited) calls
    // `new RealtimeClient(baseUrl)` with a bare string throughout. Accepting `string |
    // RealtimeOptions` keeps every one of those call sites — and the module-scope
    // `new RealtimeClient()` singleton below — working unchanged, while giving the guest
    // connection (`new RealtimeClient({ channels, deviceId, useAuth: false })`) a real options bag.
    const opts: RealtimeOptions = typeof options === 'string' ? { baseUrl: options } : options;
    this.baseUrl = opts.baseUrl ?? API_BASE_URL;
    this.channels = opts.channels;
    this.deviceId = opts.deviceId;
    this.joinCode = opts.joinCode;
    this.useAuth = opts.useAuth ?? true;
  }

  /** DESIGN.md §6.4's `offline` copy names "the exact failing host" — this is that host. */
  getTargetHost(): string {
    return hostOf(this.baseUrl);
  }

  getState(): ConnectionState {
    return this.state;
  }

  onStateChange(listener: (state: ConnectionState) => void): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  onFrame(listener: (frame: RealtimeFrame) => void): () => void {
    this.frameListeners.add(listener);
    return () => this.frameListeners.delete(listener);
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.attempt = 0;
    this.generation += 1;
    void this.connectLoop(this.generation);
  }

  stop(): void {
    this.stopped = true;
    this.generation += 1; // supersedes any loop currently mid-flight, even a zombie one
    this.controller?.abort();
    this.sleepController?.abort();
    this.clearHeartbeatTimer();
    // 'reauth' is a terminal, meaningful state (the connect loop already gave up and told the
    // caller to re-authenticate) — stop() must not paper over it with the neutral 'connecting'
    // sentinel, or a caller who reacts to 'reauth' (App.tsx's onUnauthorized -> stop()) would
    // immediately erase the very state it was reacting to.
    if (this.state !== 'reauth') {
      this.setState('connecting');
    }
  }

  private setState(next: ConnectionState): void {
    if (next === this.state) return;
    this.state = next;
    for (const listener of this.stateListeners) listener(next);
  }

  private clearHeartbeatTimer(): void {
    if (this.heartbeatTimer) clearTimeout(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private armHeartbeatTimer(): void {
    this.clearHeartbeatTimer();
    this.heartbeatTimer = setTimeout(() => this.setState('degraded'), HEARTBEAT_TIMEOUT_MS);
  }

  private async connectLoop(myGeneration: number): Promise<void> {
    while (!this.stopped && this.generation === myGeneration) {
      const unauthorized = await this.connectOnce(myGeneration);
      if (this.stopped || this.generation !== myGeneration) return; // superseded — die quietly
      if (unauthorized) {
        this.setState('reauth');
        return; // caller re-arms with start() once a fresh token exists
      }
      this.setState(this.attempt === 0 ? 'degraded' : 'offline');
      const delay = backoffDelayMs(this.attempt);
      this.attempt += 1;
      // A dedicated controller for the backoff sleep, separate from `this.controller` (which
      // belongs to the fetch/stream in connectOnce): the pre-fix code reused one shared field for
      // both, so a superseded loop's backoff-sleep controller would silently overwrite the live
      // loop's fetch controller, and a later `stop()` would abort a mere timer instead of the real
      // in-flight connection. `stop()` aborts both explicitly.
      this.sleepController = new AbortController();
      await sleep(delay, this.sleepController.signal);
    }
  }

  /** Resolves `true` on a 401 (caller should stop and re-authenticate); otherwise resolves
   * `false` once the connection ends for any other reason (server close, network drop, abort). */
  private async connectOnce(myGeneration: number): Promise<boolean> {
    const token = this.useAuth ? getToken() : null;
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (this.lastEventId) headers['Last-Event-ID'] = this.lastEventId;

    this.controller = new AbortController();

    // remote-request's guest connection sends explicit channels/deviceId (realtime controller:
    // `ctx.query.channels`/`ctx.query.deviceId`); the default operator client sends neither and
    // gets every channel its role allows, exactly as before this option existed.
    const params = new URLSearchParams();
    if (this.channels && this.channels.length > 0) params.set('channels', this.channels.join(','));
    if (this.deviceId) params.set('deviceId', this.deviceId);
    if (this.joinCode) params.set('joinCode', this.joinCode);
    // MVP-472 follow-up 2: the operator's own room, read fresh on every connect rather than
    // captured in the constructor — this client is a module-scope singleton created at import
    // time, long before the operator has picked anything, and a pick that only took effect after
    // a full page load would be a picker that looks broken for the first minute of the night.
    if (this.useAuth) {
      const room = getRoomSlug();
      if (room) params.set('room', room);
    }
    const qs = params.toString();
    const streamUrl = `${this.baseUrl}/api/realtime/stream${qs ? `?${qs}` : ''}`;

    let res: Response;
    try {
      res = await fetch(streamUrl, {
        headers,
        signal: this.controller.signal,
      });
    } catch {
      return false; // network error — let connectLoop back off and retry
    }
    if (this.generation !== myGeneration) return false; // superseded while the fetch was in flight

    if (res.status === 401 || res.status === 403) {
      // A guest connection never sent a token in the first place (useAuth === false) — there is no
      // session to refresh and no "reauth" concept on the guest page (it never renders LoginDialog).
      // Nothing to try, and (round-9 critic fix) nothing to hammer either — just fall through to
      // the ordinary non-ok/backoff path below exactly like any other failed response, so the
      // shared exponential backoff (1s/2s/4s/.../30s, `backoffDelayMs`) is what paces reconnects,
      // never an immediate retry loop.
      if (!this.useAuth) {
        console.warn(`RealtimeClient: ${streamUrl} -> ${res.status}, will retry`);
        return false;
      }
      // R9-P1-D fix (coordinator-supplied context): the KJ stream controller
      // (`realtime/controllers/realtime.js`) answers a request for KJ-only channels with 403
      // `{code:'FORBIDDEN_CHANNEL'}` when Strapi treats the caller as UNAUTHENTICATED — which is
      // exactly what happens to an EXPIRED/invalid bearer on this Public-permitted route (Strapi
      // never falls back to "anonymous but on an allowed channel", it just drops the token and
      // re-runs the permission check as anonymous). So an expired KJ JWT on this stream shows up
      // as 403 FORBIDDEN_CHANNEL, not 401 — worth exactly the same one-shot refresh-and-retry as a
      // real 401. Only treat it that way when a token was actually sent AND the body is genuinely
      // Strapi's JSON error envelope: a 403 from something else entirely (an edge/CDN block page,
      // typically HTML, not JSON) is not an auth failure and must just back off like any other
      // non-ok response — retrying it against a "refreshed" token would never help and would spend
      // a real refresh call for nothing.
      let isAuthFailure = res.status === 401;
      if (res.status === 403 && token) {
        try {
          const body = (await res.json()) as StrapiErrorBody;
          isAuthFailure = typeof body?.error?.message === 'string' || typeof body?.error?.name === 'string';
        } catch {
          isAuthFailure = false; // non-JSON body (e.g. an HTML block page) — not an auth failure
        }
      }
      if (!isAuthFailure) {
        console.warn(`RealtimeClient: ${streamUrl} -> 403, will retry`);
        return false;
      }
      // P2 fix (round 4): this connection's own artificial start delay (`REALTIME_START_DELAY_MS`
      // in App.tsx) plus real network latency to the live backend means the page-load burst of
      // ordinary `apiFetch` calls (listSingers/useSessionInfo/searchCatalog/...) routinely finishes
      // ITS OWN 401-triggered refresh before this stream's delayed attempt even fails — confirmed
      // live: `POST /api/auth/refresh` firing twice on one page load. If storage already holds a
      // DIFFERENT token than the one this failed request used, someone else already refreshed while
      // we were in flight — just let `connectLoop`'s retry pick it up via a fresh `getToken()`
      // rather than spending a second, wholly redundant refresh call (and rotating the one-time-use
      // refresh token again for no reason).
      if (getToken() !== token) return false;
      // R10-P2-1 fix: this is OUR SECOND-OR-LATER auth failure in the same outage — a prior
      // attempt already spent its one refresh (see `refreshedThisOutage` doc comment above).
      // Refreshing again here would just rotate the refresh token on every backoff step forever,
      // since the server keeps answering 401/403 for whatever reason caused the outage in the
      // first place. Just back off like any other failed attempt and let the flag's owner (a
      // `hello` frame) decide when it's safe to try a fresh refresh again.
      if (this.refreshedThisOutage) return false;
      // VPS-10: the stream's access token is the same 60-minute one `apiFetch` refreshes — a 401
      // (or, per the above, a FORBIDDEN_CHANNEL 403) here usually just means it lapsed while this
      // connection sat open, not that the session is actually dead. Piggyback on `client.ts`'s
      // shared, dedup'd refresh (a REST call may already have triggered it) and let the next
      // `connectOnce()` (called fresh with `getToken()`, right above) pick up the new token,
      // instead of jumping straight to the terminal 'reauth' state that forces a full re-login
      // every hour.
      if (getRefreshToken()) {
        try {
          this.refreshedThisOutage = true;
          await refreshAccessToken(this.baseUrl);
          return false; // connectLoop backs off briefly, then retries with the refreshed token
        } catch {
          // refresh failed too — this really is a dead session
        }
      }
      return true;
    }
    if (!res.ok || !res.body) {
      // P2 fix (round 3): a bare `return false` here was indistinguishable from a normal closed
      // connection — confirmed live, `/api/realtime/stream` 503s on the first attempt of a fresh
      // page load and this loop's own backoff (attempt 0 -> ~1s) reconnects and succeeds so fast
      // the failure was never visible anywhere (defect: "client retry masks it"). The backoff
      // itself is correct behaviour and stays; this just stops it from being silent. See
      // client.ts's matching comment on `apiFetch`'s own 5xx log.
      if (!res.ok) console.warn(`RealtimeClient: ${streamUrl} -> ${res.status}, will retry`);
      return false;
    }

    this.attempt = 0;
    this.setState('online');
    this.armHeartbeatTimer();

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (this.generation !== myGeneration) {
          // Superseded mid-stream (a fresh start() raced us) — stop reading, never touch shared
          // state again. `stop()` already aborted `this.controller`, which is normally how we get
          // here via a thrown read(), but this guard covers the rare case where a frame arrives in
          // the same tick as the supersession.
          await reader.cancel().catch(() => {});
          return false;
        }
        this.armHeartbeatTimer(); // any bytes at all — including ":hb\n\n" — reset the timeout
        buffer += decoder.decode(value, { stream: true });

        let boundary: number;
        while ((boundary = buffer.indexOf('\n\n')) !== -1) {
          const block = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const frame = parseSSEBlock(block);
          if (frame) {
            // R10-P2-1 fix: a `hello` frame is the server's proof this outage is over (SPEC: sent
            // once per successful `connectOnce()`) — only then is it safe to spend a fresh
            // refresh on a LATER auth failure. Deliberately not reset merely by reaching the
            // `online` state above: `hello` is the first frame of the body, so a connection that
            // gets torn down before it ever arrives never earns the flag back.
            if (frame.event === 'hello') this.refreshedThisOutage = false;
            // Only a frame that actually carried a wire `id:` line advances the resume cursor —
            // matching native EventSource's own contract, and SPEC's hello frame deliberately
            // omits `id:` (strapi-template/src/api/realtime/services/realtime.js, Notion task 47)
            // precisely because it is not itself a ring-buffer position. Round-2 client fix: this
            // used to key off `frame.seq`, which for `hello` is populated straight from its JSON
            // `payload.seq` (the ring's CURRENT head at connect time, not an event this client has
            // actually consumed) — so `lastEventId` jumped ahead of anything replayed right after
            // it, and a drop before that replay landed lost those frames permanently with no
            // `resync.required` to say so, even after the server-side hello/id ordering fix.
            if (/^id: /m.test(block)) {
              this.lastEventId = String(frame.seq);
            }
            for (const listener of this.frameListeners) listener(frame);
          }
        }
      }
    } catch {
      // aborted or the connection dropped mid-stream — fall through to reconnect
    } finally {
      this.clearHeartbeatTimer();
    }

    return false;
  }
}

/** One instance for the whole app (DESIGN.md §6.4: "the indicator lives in the status bar and
 * nowhere else"). App.tsx starts it after a successful login and stops it on logout. */
export const realtimeClient = new RealtimeClient();
